import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Factory, ArrowLeft, Mail, CheckCircle2, Loader2 } from "lucide-react";

const HERO = "https://images.pexels.com/photos/8956445/pexels-photo-8956445.jpeg";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="flex flex-col px-6 py-8 lg:px-16">
        <Link to="/" className="flex items-center gap-2">
          <div className="h-7 w-7 bg-primary grid place-items-center rounded-sm"><Factory className="h-4 w-4 text-primary-foreground" /></div>
          <span className="font-heading font-extrabold text-lg tracking-tight">Nepton</span>
        </Link>

        <div className="flex-1 flex flex-col justify-center max-w-md w-full mx-auto">
          {sent ? (
            <div data-testid="forgot-success">
              <div className="h-12 w-12 bg-emerald-50 border border-emerald-200 grid place-items-center rounded-sm">
                <CheckCircle2 className="h-6 w-6 text-emerald-600" />
              </div>
              <h1 className="mt-5 text-3xl font-bold tracking-tight">Check your inbox</h1>
              <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
                If an account exists for <span className="font-medium text-foreground">{email}</span>, we've sent a password reset link. It expires in <strong>1 hour</strong>.
              </p>
              <p className="mt-2 text-sm text-muted-foreground">Didn't get it? Check spam, or <button onClick={() => setSent(false)} className="text-brand hover:underline" data-testid="resend-link">try again</button>.</p>
              <Link to="/login" className="mt-8 inline-flex items-center gap-1.5 text-sm text-brand hover:underline"><ArrowLeft className="h-4 w-4" /> Back to sign in</Link>
            </div>
          ) : (
            <>
              <div className="label-tech text-brand mb-3">Password reset</div>
              <h1 className="text-3xl font-bold tracking-tight">Forgot your password?</h1>
              <p className="mt-2 text-sm text-muted-foreground">Enter your account email and we'll send you a link to reset it.</p>

              <form onSubmit={submit} className="mt-8 space-y-4">
                <label className="block">
                  <span className="label-tech text-muted-foreground block mb-1.5">Email</span>
                  <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="forgot-email" className="fp-input" placeholder="you@company.com" />
                </label>
                {error && <div data-testid="forgot-error" className="text-sm text-destructive border border-destructive/30 bg-destructive/5 px-3 py-2 rounded-sm">{error}</div>}
                <button type="submit" disabled={loading} data-testid="forgot-submit"
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-brand text-white rounded-sm hover:bg-brand-dark disabled:opacity-60 transition-colors">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
                  {loading ? "Sending…" : "Send reset link"}
                </button>
              </form>
              <Link to="/login" className="mt-6 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"><ArrowLeft className="h-4 w-4" /> Back to sign in</Link>
            </>
          )}
        </div>
      </div>

      <div className="hidden lg:block relative border-l border-border">
        <img src={HERO} alt="Manufacturing" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-primary/70" />
      </div>

      <style>{`.fp-input{width:100%;border:1px solid hsl(var(--input));background:hsl(var(--card));padding:0.65rem 0.85rem;border-radius:0.25rem;font-size:0.9rem;outline:none;transition:border-color .15s}.fp-input:focus{border-color:#EA580C}`}</style>
    </div>
  );
}
